import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { FaChevronDown } from 'react-icons/fa';

/**
 * Dropdown menu component to replace MUI Menu
 */
const DropdownMenu = ({ 
  trigger, 
  items, 
  position = 'bottom-right',
  className = '' 
}) => {
  const { semantic } = useColors();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        triggerRef.current &&
        !menuRef.current.contains(event.target) &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const positionClasses = {
    'bottom-right': 'top-full right-0 mt-1',
    'bottom-left': 'top-full left-0 mt-1',
    'top-right': 'bottom-full right-0 mb-1',
    'top-left': 'bottom-full left-0 mb-1'
  };

  return (
    <div className={`relative ${className}`}>
      <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </div>
      
      {isOpen && (
        <div
          ref={menuRef}
          className={`absolute z-50 min-w-[160px] ${semantic.contentCardBg} ${semantic.contentCardBorder} rounded-md shadow-lg py-1 ${positionClasses[position]}`}
          role="menu"
          aria-orientation="vertical"
        >
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                if (item.onClick) {
                  item.onClick();
                }
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm ${semantic.contentCardText} hover:${semantic.sectionBg} transition-colors ${
                item.danger ? semantic.errorText : ''
              } ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={item.disabled}
              role="menuitem"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

DropdownMenu.propTypes = {
  trigger: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      danger: PropTypes.bool,
      disabled: PropTypes.bool
    })
  ).isRequired,
  position: PropTypes.oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left']),
  className: PropTypes.string
};

export default DropdownMenu;

